import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Card, CardActionArea, Typography, Grid, Stack, Avatar, Chip,
  CircularProgress, Alert,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  School as ExamIcon,
  Payments as CollectionsIcon,
  HowToReg as RegisterIcon,
  ChevronRight as ChevronRightIcon,
  Lock as LockIcon,
  Inbox as InboxIcon,
  Visibility as PreviewIcon,
} from '@mui/icons-material';
import API from '../BaseURL';
import useViewAs from './useViewAs';

// Tile visual — one card. `disabledReason` (when set) greys the card
// and shows why it's not clickable (e.g. period elapsed).
function Tile({ tile, disabledReason, onOpen }) {
  const Icon = tile.icon;
  const isDisabled = !!disabledReason;
  return (
    <Card sx={{
      borderRadius: 3, overflow: 'hidden', height: '100%',
      opacity: isDisabled ? 0.55 : 1,
      boxShadow: isDisabled
        ? '0 4px 12px -8px rgba(30,42,120,0.15)'
        : '0 8px 22px -14px rgba(30,42,120,0.28)',
      transition: 'transform 0.15s, box-shadow 0.15s',
      '&:hover': isDisabled ? {} : {
        transform: 'translateY(-2px)',
        boxShadow: '0 16px 30px -14px rgba(30,42,120,0.35)',
      },
    }}>
      <CardActionArea disabled={isDisabled} onClick={onOpen}
        sx={{ height: '100%', p: 0 }}>
        <Box sx={{
          height: { xs: 68, md: 84 },
          background: isDisabled
            ? `linear-gradient(135deg, #b0bec5 0%, #78909c 100%)`
            : `linear-gradient(135deg, ${tile.tone.from} 0%, ${tile.tone.to} 100%)`,
          display: 'flex', alignItems: 'center',
          px: { xs: 1.6, md: 2.2 }, gap: { xs: 1.2, md: 1.5 },
        }}>
          <Avatar sx={{
            bgcolor: 'rgba(255,255,255,0.22)',
            border: '2px solid rgba(255,255,255,0.55)',
            width: { xs: 40, md: 48 }, height: { xs: 40, md: 48 },
          }}>
            <Icon sx={{ color: '#fff' }} />
          </Avatar>
          <Typography noWrap sx={{
            color: '#fff', fontWeight: 900, fontSize: { xs: 17, md: 20 },
            textShadow: '0 1px 2px rgba(0,0,0,0.18)', minWidth: 0,
          }}>
            {tile.title}
          </Typography>
          <Box sx={{ flex: 1 }} />
          {isDisabled
            ? <LockIcon sx={{ color: 'rgba(255,255,255,0.85)' }} />
            : <ChevronRightIcon sx={{ color: 'rgba(255,255,255,0.85)' }} />}
        </Box>
        <Box sx={{ p: { xs: 1.6, md: 2.2 } }}>
          <Typography variant="body2" color="text.secondary"
            sx={{ lineHeight: 1.5, fontSize: { xs: 13, md: 14 } }}>
            {tile.blurb}
          </Typography>
          {disabledReason && (
            <Chip size="small" color="warning" variant="outlined"
              label={disabledReason} icon={<LockIcon fontSize="small" />}
              sx={{ mt: 1.2, fontWeight: 700 }} />
          )}
        </Box>
      </CardActionArea>
    </Card>
  );
}

export default function TeacherPortalHub() {
  const nav = useNavigate();
  const loc = useLocation();
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');
  const [exam, setExam]                 = useState(null);        // /me response
  const [collections, setCollections]   = useState([]);          // collector assignments
  const [colPeriod, setColPeriod]       = useState(null);        // term window for collections
  const [register, setRegister]         = useState(null);        // register duty + today's state

  // ?view_as=<id> — an admin previewing a teacher's portal, from the
  // Teacher Portal Preview on Examination Settings.
  //
  // It has to be FORWARDED to every endpoint and CARRIED through every
  // link out of this page. Doing neither was the bug: the hub asked the
  // three endpoints about the admin instead of the teacher, so the
  // Examination card disappeared (an admin has no assignments of their
  // own) while the Register card filled with the admin's see-everything
  // duty — "14 of your 14 classes" under the teacher's name. And
  // because the tiles navigated without the param, TeacherGuard saw a
  // non-teacher with no view_as and bounced straight to /dashboard,
  // which is the flash-and-return on tapping a card.
  const { withViewAs, viewAsParams, viewAsId } = useViewAs();

  const load = async () => {
    setLoading(true); setError('');
    try {
      // Endpoints are auth-scoped to the caller, or to the previewed
      // teacher when view_as is present and the caller may use it.
      // Run in parallel; a failure on one side doesn't block the other.
      // For collections we use the *same* endpoint the Collections page
      // uses (/me/daily-income-types) so the card only appears when the
      // page will actually have something to show — no more "card visible
      // but page is empty" mismatch.
      const [meRes, colRes, regRes] = await Promise.allSettled([
        API.get('/teacher-assignments/me',            { params: viewAsParams }),
        API.get('/collections/me/daily-income-types', { params: viewAsParams }),
        API.get('/register/me',                       { params: viewAsParams }),
      ]);
      if (meRes.status === 'fulfilled')  setExam(meRes.value.data || null);
      if (colRes.status === 'fulfilled') {
        // Response shape is { date, rows: [...], acc_yer, period_open,
        // period_message }. The rows decide whether the card shows;
        // period_open decides whether it is clickable, exactly as it
        // does for Examination.
        setCollections(colRes.value.data?.rows || []);
        setColPeriod(colRes.value.data || null);
      }
      // { acc_yer, term, date, classes: [{ cla, total, marked, ... }] }.
      // The classes are what decide whether the card shows at all, and
      // how many still need marking today.
      if (regRes.status === 'fulfilled') setRegister(regRes.value.data || null);
      if (meRes.status === 'rejected' && colRes.status === 'rejected'
          && regRes.status === 'rejected') {
        setError(meRes.reason?.response?.data?.message || 'Could not load your assignments');
      }
    } finally {
      setLoading(false);
    }
  };

  // Refetch on hub navigation so returning from a sub-page picks up any
  // changes an admin made in the meantime (assignments granted/revoked,
  // period rolled over, collections added).
  // `loc.search` is in the deps as well as the path: switching to a
  // different teacher in the preview changes only ?view_as, and without
  // it the hub would keep showing the previous teacher's cards.
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [loc.pathname, loc.search]);

  // ── Visibility gates ─────────────────────────────────────────
  const examHasWork = useMemo(() => {
    if (!exam) return false;
    return (exam.assignments?.length > 0) || (exam.class_teacher_classes?.length > 0);
  }, [exam]);
  const examPeriodOpen = exam?.period_open !== false;   // default open when unknown
  const examEndDate    = exam?.end_dat ? String(exam.end_dat).slice(0, 10) : '';

  const showExamCard      = examHasWork;                            // hide entirely when unassigned
  // Examination runs every day unless an administrator turns a day off
  // (Settings → Academics → School Week) — teachers mark at the weekend
  // and that has to keep working. When one IS turned off, say so rather
  // than blaming the editing window.
  const examOffDay = exam?.period_block_kind === 'non_school_day';
  const examDisabledReason = !(examHasWork && !examPeriodOpen) ? ''
    : examOffDay
      ? `No score entry on ${exam.day_name ? `a ${exam.day_name}` : 'this day'}`
      : `Editing window closed${examEndDate ? ` on ${examEndDate}` : ''}`;

  const showCollectionsCard = collections.length > 0;               // hide entirely when unassigned
  // Collections are scoped to the academic year and term now, so the
  // card locks when the window shuts instead of sitting open beside a
  // locked Examination card — which was the tell that the whole module
  // was ignoring the school calendar.
  const colPeriodOpen  = colPeriod?.period_open !== false;          // default open when unknown
  const colEndDate     = colPeriod?.end_dat ? String(colPeriod.end_dat).slice(0, 10) : '';
  // An off day and a lapsed term both grey the card out, but they are
  // not the same news: one is over tomorrow, the other needs an
  // administrator. The server says which (period_block_kind).
  const colOffDay = colPeriod?.period_block_kind === 'non_school_day';
  const colDisabledReason = !(showCollectionsCard && !colPeriodOpen) ? ''
    : colOffDay
      ? `No collections on ${colPeriod.day_name ? `a ${colPeriod.day_name}` : 'this day'}`
      : `Term closed${colEndDate ? ` on ${colEndDate}` : ''} — cannot record cash`;

  // Register duty — same rule: no class assigned, no card. The blurb
  // carries the one fact a teacher wants before tapping in, which is
  // how many of their classes are still unmarked today.
  const regClasses = register?.classes || [];
  const regPending = regClasses.filter(c => !c.marked).length;
  const showRegisterCard = regClasses.length > 0;
  // The register is stamped with the year and term, so it locks when
  // that window shuts — a teacher assigned a class during a closed term
  // sees the card, sees why it is shut, and cannot open it. Leaving it
  // green and clickable would have promised a sheet that refuses to save.
  const regPeriodOpen = register?.period_open !== false;   // default open when unknown
  const regEndDate    = register?.end_dat ? String(register.end_dat).slice(0, 10) : '';
  const regOffDay = register?.period_block_kind === 'non_school_day';
  const regDisabledReason = !(showRegisterCard && !regPeriodOpen) ? ''
    : regOffDay
      ? `No register on ${register.day_name ? `a ${register.day_name}` : 'this day'}`
      : `Term closed${regEndDate ? ` on ${regEndDate}` : ''} — cannot mark the register`;

  const tiles = [];
  if (showExamCard) {
    tiles.push({
      key: 'examination',
      tile: {
        key:   'examination',
        title: 'Examination',
        blurb: 'Class remarks, exam report cards, and term / mid-term / mock score entry.',
        icon:  ExamIcon,
        tone:  { from: '#7986cb', to: '#3f51b5' },
      },
      disabledReason: examDisabledReason,
      onOpen: () => nav(withViewAs('/teacher-portal/examination')),
    });
  }
  if (showCollectionsCard) {
    tiles.push({
      key: 'collections',
      tile: {
        key:   'collections',
        title: 'Collections',
        blurb: `You have ${collections.length} collection route${collections.length === 1 ? '' : 's'} assigned`
             + `${colPeriod?.acc_yer ? ` for ${colPeriod.acc_yer}` : ''} — mark attendance and record cash.`,
        icon:  CollectionsIcon,
        tone:  { from: '#4dd0e1', to: '#00838f' },
      },
      disabledReason: colDisabledReason,
      onOpen: () => nav(withViewAs('/teacher-portal/collections')),
    });
  }
  if (showRegisterCard) {
    tiles.push({
      key: 'register',
      tile: {
        key:   'register',
        title: 'Register',
        // While the term is shut there is no "today" to be behind on,
        // so the blurb states what the card covers instead of nagging
        // about classes nobody can mark.
        blurb: !regPeriodOpen
          ? `You take the register for ${regClasses.length} class${regClasses.length === 1 ? '' : 'es'}`
            + `${register?.acc_yer ? ` in ${register.acc_yer}` : ''} — girls and boys, with daily and term totals.`
          : regPending === 0
            ? `Today's register is in for all ${regClasses.length} of your class${regClasses.length === 1 ? '' : 'es'}.`
            : `${regPending} of your ${regClasses.length} class${regClasses.length === 1 ? '' : 'es'} still to mark today.`,
        icon:  RegisterIcon,
        tone:  { from: '#81c784', to: '#2e7d32' },
      },
      disabledReason: regDisabledReason,
      onOpen: () => nav(withViewAs('/teacher-portal/register')),
    });
  }

  if (loading) {
    return (
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress size={26} />
      </Box>
    );
  }
  if (error) {
    return <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>;
  }

  // Whose portal is this? During a preview the banner above still
  // carries the ADMIN's name and role — that is the signed-in account,
  // and it cannot say otherwise. Left unmarked, the page reads as the
  // admin's own portal showing a teacher's work, which is exactly how
  // this looked when it was broken. So the preview names itself.
  const previewOf = exam?.view_as || null;

  return (
    <Box>
      {viewAsId && (
        <Alert severity="info" variant="outlined" icon={<PreviewIcon />}
          sx={{ borderRadius: 2, mb: 2, fontWeight: 600 }}>
          Previewing the portal of <b>{previewOf?.name || previewOf?.email || `user #${viewAsId}`}</b>
          {' '}— this is what they see. Nothing you open here is recorded against them.
        </Alert>
      )}
      <Stack direction="row" alignItems="center" spacing={1.2} sx={{ mb: 2 }}>
        <Typography variant="overline" fontWeight={800} color="text.secondary"
          sx={{ letterSpacing: 1.2 }}>
          What would you like to do?
        </Typography>
        <Chip size="small" variant="outlined"
          label={tiles.length === 0
            ? 'nothing assigned'
            : `${tiles.length} area${tiles.length === 1 ? '' : 's'}`}
          sx={{ fontWeight: 700 }} />
      </Stack>

      {tiles.length === 0 ? (
        <Card sx={{ borderRadius: 3, p: { xs: 3, md: 5 }, textAlign: 'center' }}>
          <InboxIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1.5 }} />
          <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5 }}>
            Nothing assigned to you yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your school admin controls what appears here. Ask them to assign you
            subjects (for examination) or a collection route (for daily cash
            collection), and this page will fill in automatically.
          </Typography>
          {examHasWork && !examPeriodOpen && (
            <Chip color="warning" variant="outlined" icon={<LockIcon fontSize="small" />}
              label={`Examination editing closed${examEndDate ? ` on ${examEndDate}` : ''} — reopen the term to unlock`}
              sx={{ fontWeight: 700, mt: 1 }} />
          )}
        </Card>
      ) : (
        <Grid container spacing={{ xs: 1.5, md: 2.5 }}>
          {tiles.map(t => (
            <Grid key={t.key} item xs={12} sm={6} md={4}>
              <Tile tile={t.tile} disabledReason={t.disabledReason} onOpen={t.onOpen} />
            </Grid>
          ))}
        </Grid>
      )}

      {tiles.length > 0 && (
        <Box sx={{
          mt: 3, p: 2, borderRadius: 2,
          bgcolor: (t) => alpha(t.palette.primary.main, 0.05),
          border: '1px dashed', borderColor: 'primary.light',
        }}>
          <Typography variant="caption" color="text.secondary">
            Tip — the header shows a <b>Back to menu</b> button once you're inside a section,
            so you can hop between areas without signing out.
          </Typography>
        </Box>
      )}
    </Box>
  );
}

